import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Star } from "lucide-react";
import { HERO } from "@/constants/testIds";

const HERO_FAMILIES = [
  {
    src: "https://images.unsplash.com/photo-1529518152792-d08317b26e22?w=1200&auto=format&fit=crop&q=70",
    alt: "Family walking through sunlit trees",
    quote: "\u201cWe slept better the night we signed.\u201d",
  },
  {
    src: "https://plus.unsplash.com/premium_photo-1661490665324-7d67c67be2a3?w=1200&auto=format&fit=crop&q=70",
    alt: "Parents in a sunlit park with their daughter on dad's shoulders",
    quote: "\u201cOur kids will be okay, no matter what.\u201d",
  },
  {
    src: "https://plus.unsplash.com/premium_photo-1661690544174-bb23e2e67d5d?w=1200&auto=format&fit=crop&q=70",
    alt: "Family enjoying time together on a city walk",
    quote: "\u201cAffordable, honest, and finally on paper.\u201d",
  },
  {
    src: "https://plus.unsplash.com/premium_photo-1682095261811-676fc6d0ad80?w=1200&auto=format&fit=crop&q=70",
    alt: "Family enjoying a picnic on the grass",
    quote: "\u201cPeace of mind worth every penny.\u201d",
  },
  {
    src: "https://plus.unsplash.com/premium_photo-1726797766978-11a0b18dc341?w=1200&auto=format&fit=crop&q=70",
    alt: "Family laughing together at home",
    quote: "\u201cA gift to the people we love most.\u201d",
  },
];

export default function Hero() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % HERO_FAMILIES.length);
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid={HERO.section}
      className="relative pt-28 sm:pt-32 pb-16 lg:pt-40 lg:pb-24"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-7 animate-fade-up">
          <p className="overline mb-6">Independent · A-rated carriers · Family-owned</p>
          <h1 className="font-serif text-[2.6rem] sm:text-6xl lg:text-7xl xl:text-[5.2rem] leading-[1.04] tracking-tight text-primary">
            The love you leave behind
            <br />
            <em className="not-italic text-accent">deserves a plan.</em>
          </h1>
          <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-muted-foreground">
            Faith Family Finance helps families compare life insurance from 25+ top-rated carriers
            — without sales pressure, hidden fees, or jargon. Just real coverage for the people you love most.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              data-testid={HERO.primaryCta}
              size="lg"
              onClick={() => scrollTo("quote")}
              className="rounded-full h-12 px-7 bg-primary hover:bg-primary/90 text-base group"
            >
              Get my free quote
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              data-testid={HERO.secondaryCta}
              size="lg"
              variant="outline"
              onClick={() => scrollTo("plans")}
              className="rounded-full h-12 px-7 border-border bg-transparent hover:bg-muted text-foreground"
            >
              Explore plans
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-accent" strokeWidth={1.5} />
              Licensed in all 50 states
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 fill-accent text-accent" strokeWidth={1.5} />
              4.9 / 5 from 2,400+ families
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 animate-fade-up [animation-delay:120ms]">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-accent/15 blur-2xl" />
            <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full bg-primary/10 blur-3xl" />
            <div className="relative overflow-hidden rounded-[2rem] aspect-[4/5] soft-shadow bg-muted">
              {HERO_FAMILIES.map((f, i) => (
                <img
                  key={f.src}
                  src={f.src}
                  alt={f.alt}
                  loading={i === 0 ? "eager" : "lazy"}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                    i === index ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/45 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-6 left-6 right-6 text-primary-foreground">
                <p className="text-xs uppercase tracking-[0.22em] opacity-80">A real family, a real plan</p>
                <p
                  key={index}
                  className="font-serif text-xl sm:text-2xl leading-tight mt-2 italic animate-fade-up"
                >
                  {HERO_FAMILIES[index].quote}
                </p>
              </div>

              {/* dots */}
              <div className="absolute top-5 right-5 flex gap-1.5">
                {HERO_FAMILIES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    aria-label={`Show family ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === index ? "w-6 bg-primary-foreground" : "w-1.5 bg-primary-foreground/40 hover:bg-primary-foreground/70"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
